import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

import { T } from "../../constants";
import { useUi } from "../../ui";
import Field from "../../vip/components/Field";
import {
  DEFAULT_SHIFT,
  SHIFT_GRACE_PERIOD_OPTIONS,
  SHIFT_TIME_OPTIONS,
  SLOT_MINUTES_OPTIONS,
} from "../constants";
import { validateShift } from "../shiftHelpers";

const useStyles = makeStyles({
  title: { margin: 0, fontSize: 16, fontWeight: 600, color: T.fg },
  body: { marginTop: 16, display: "grid", gap: 12 },
  toggle: { display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: T.fg },
  hint: { fontSize: 12, color: T.mutedFg },
  error: { fontSize: 12, color: T.danger },
});

export default function AddShiftForm({ disabled, onAdd }) {
  const s = useStyles();
  const ui = useUi();
  const [shift, setShift] = useState(DEFAULT_SHIFT);
  const [submitting, setSubmitting] = useState(false);

  const setField = (field) => (event) => {
    const { value } = event.target;
    setShift((current) => ({ ...current, [field]: value }));
  };

  /* Select values arrive as strings; the API takes Int. */
  const setMinutesField = (field) => (event) => {
    const value = parseInt(event.target.value, 10);
    setShift((current) => ({ ...current, [field]: value }));
  };

  /* An emptied box stays empty rather than becoming NaN; validateShift rejects it. */
  const setCountField = (field) => (event) => {
    const { value } = event.target;
    setShift((current) => ({ ...current, [field]: value === "" ? "" : Number(value) }));
  };

  const toggleActive = (event) => {
    const { checked } = event.target;
    setShift((current) => ({ ...current, isActive: checked }));
  };

  const errors = validateShift(shift);
  const firstError = Object.values(errors)[0];
  const isBlocked = disabled || submitting;
  const canSubmit = !firstError && !isBlocked;
  const isDirty = JSON.stringify(shift) !== JSON.stringify(DEFAULT_SHIFT);

  const submit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      const saved = await onAdd(shift);
      if (saved !== false) setShift(DEFAULT_SHIFT);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <h2 className={s.title}>Add shift</h2>
      <form className={s.body} onSubmit={submit}>
        <Field label="Name">
          <input value={shift.name} disabled={isBlocked} onChange={setField("name")} placeholder="Day Shift" className={ui.field} />
        </Field>
        <Field label="Start time">
          <select value={shift.startTime} disabled={isBlocked} onChange={setMinutesField("startTime")} className={ui.field}>
            {SHIFT_TIME_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </Field>
        <Field label="End time">
          <select value={shift.endTime} disabled={isBlocked} onChange={setMinutesField("endTime")} className={ui.field}>
            {SHIFT_TIME_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Grace period">
          <select value={shift.gracePeriodMinutes} disabled={isBlocked} onChange={setMinutesField("gracePeriodMinutes")} className={ui.field}>
            {SHIFT_GRACE_PERIOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Max pickups per period">
          <input value={shift.maxCollectPerSlot} disabled={isBlocked} onChange={setCountField("maxCollectPerSlot")} type="number" min="1" className={ui.field} />
        </Field>
        <Field label="Max to park per period">
          <input value={shift.maxParkPerSlot} disabled={isBlocked} onChange={setCountField("maxParkPerSlot")} type="number" min="1" className={ui.field} />
        </Field>
        <Field label="Period length">
          <select value={shift.slotMinutes} disabled={isBlocked} onChange={setMinutesField("slotMinutes")} className={ui.field}>
            {SLOT_MINUTES_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Description">
          <input value={shift.description} disabled={isBlocked} onChange={setField("description")} placeholder="Short internal note" className={ui.field} />
        </Field>

        <label className={s.toggle}>
          <input type="checkbox" disabled={isBlocked} checked={shift.isActive} onChange={toggleActive} />
          Active
        </label>

        {Boolean(firstError) && isDirty && (
          <div className={s.error}>{firstError}</div>
        )}

        <button type="submit" disabled={!canSubmit} className={ui.primaryBtn} style={{ width: "100%" }}>
          Add shift
        </button>
      </form>
    </div>
  );
}
